import React from "react";
import Chapter from "@/components/landing/Chapter";
import Reveal from "@/components/landing/Reveal";
import EditorialButton from "@/components/landing/EditorialButton";
import { STRIPE_LINKS, EVENT } from "@/lib/landingConfig";
import { useReservation } from "@/components/landing/ReservationModal";

const generalIncludes = [
  "Asistencia al seminario presencial del 15 de octubre.",
  "Desayuno y almuerzo networking.",
  "Acceso gratuito durante 3 meses al Mastermind.",
  "Dos sesiones mensuales online.",
  "Beneficios y descuentos por pertenecer a la comunidad.",
  "Acceso a espacios de visibilidad en medios y eventos empresariales.",
];

const vipExtras = [
  "Welcome Pack personalizado.",
  "Acceso prioritario.",
  "Sesiones exclusivas online con las ponentes tras el evento.",
  "Acceso a la fiesta Primer Aniversario.",
  "Crucero nocturno por el Guadalquivir.",
  "Cena con cata de vinos exclusiva con ponentes, dirigida por Mónica Rosón “La Cataora”.",
];

const Card = ({ children, highlight = false, hideDefaultBadge = false, testId }) => (
  <div
    data-testid={testId}
    className={`relative flex flex-col h-full p-8 md:p-10 lg:p-12 border ${
      highlight
        ? "bg-[#3F403A] text-[#FAF8F3] border-[#3F403A]"
        : "bg-[#FAF8F3] text-[#3F403A] border-[#3F403A]/15"
    }`}
  >
    {highlight && !hideDefaultBadge && (
      <span
        className="absolute -top-3 left-8 md:left-10 bg-[#B77A4B] text-[#FAF8F3] overline px-3 py-1.5"
        data-testid="pricing-vip-badge"
      >
        Experiencia completa
      </span>
    )}
    {children}
  </div>
);

const Pricing = () => {
  const { open: openReservation } = useReservation();
  return (
    <section
      id="inscripcion"
      data-testid="pricing-section"
      className="bg-[#F4EFE7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 lg:py-40">
        <div className="max-w-4xl">
          <Reveal>
            <Chapter number="09" label="Inscripción" />
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#3F403A]">
              Elige cómo quieres vivir{" "}
              <span className="font-quiche text-[#63846F]">
                Lidera con Voz <strong className="quiche-bold font-extrabold">Propia</strong>
              </span>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Pase General */}
          <Reveal delay={80} className="h-full" data-testid="pricing-general">
            <Card testId="pricing-general-card">
              {EVENT.generalBatchClosed && (
                <span
                  className="absolute -top-3 left-8 md:left-10 bg-[#63846F] text-[#FAF8F3] overline px-3 py-1.5"
                  data-testid="pricing-general-soldout-badge"
                >
                  Último lote en apertura
                </span>
              )}
              <p className="overline text-[#63846F]">Pase General</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span
                  className={`font-serif text-6xl md:text-7xl leading-none ${
                    EVENT.generalBatchClosed ? "text-[#3F403A]/50" : "text-[#3F403A]"
                  }`}
                >
                  297 €
                </span>
                <span className="overline text-[#3F403A]/60">IVA incl.</span>
              </div>
              <p className="mt-8 font-sans text-sm text-[#3F403A]/70 uppercase tracking-[0.2em] font-medium">
                Incluye
              </p>
              <ul className={`mt-5 space-y-4 border-t border-[#3F403A]/12 ${EVENT.generalBatchClosed ? "opacity-80" : ""}`}>
                {generalIncludes.map((it, i) => (
                  <li
                    key={i}
                    className="pt-4 flex gap-4 border-b border-[#3F403A]/8 pb-4 last:border-b-0"
                    data-testid={`pricing-general-item-${i}`}
                  >
                    <span className="mt-2 h-1.5 w-1.5 bg-[#63846F] shrink-0" aria-hidden />
                    <span className="font-sans text-base leading-relaxed text-[#3F403A]/90">
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                {EVENT.generalBatchClosed ? (
                  <div className="space-y-4" data-testid="pricing-general-batch-closed">
                    <div
                      className="w-full inline-flex items-center justify-center gap-3 border border-[#3F403A]/25 bg-transparent text-[#3F403A]/60 px-8 py-4 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium cursor-not-allowed select-none"
                      aria-disabled="true"
                    >
                      Reservas temporalmente cerradas
                    </div>
                    <p className="font-sans text-xs md:text-sm italic text-[#3F403A]/70 text-center leading-relaxed">
                      Se ha cerrado el segundo lote de inscripciones con
                      condiciones especiales. En unas semanas se anunciará el
                      último lote de plazas disponibles.
                    </p>
                    <a
                      href="#info"
                      className="block text-center overline text-[#63846F] hover:text-[#3F403A] transition-colors"
                      data-testid="pricing-general-waitlist"
                    >
                      Avísame del último lote →
                    </a>
                  </div>
                ) : (
                  <EditorialButton
                    as="button"
                    type="button"
                    onClick={() => openReservation(STRIPE_LINKS.general, "Pase General")}
                    variant="jade"
                    className="w-full"
                    data-testid="pricing-general-cta"
                  >
                    Reservar Pase General
                  </EditorialButton>
                )}
              </div>
            </Card>
          </Reveal>

          {/* Experiencia VIP */}
          <Reveal delay={180} className="h-full" data-testid="pricing-vip">
            <Card highlight hideDefaultBadge={EVENT.vipSoldOut} testId="pricing-vip-card">
              {EVENT.vipSoldOut && (
                <span
                  className="absolute -top-3 left-8 md:left-10 bg-[#B77A4B] text-[#FAF8F3] overline px-3 py-1.5"
                  data-testid="pricing-vip-soldout-badge"
                >
                  Aforo completo
                </span>
              )}
              <p className="overline text-[#B77A4B]">Experiencia VIP</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span
                  className={`font-serif text-6xl md:text-7xl leading-none ${
                    EVENT.vipSoldOut ? "text-[#FAF8F3]/45 line-through decoration-1" : "text-[#FAF8F3]"
                  }`}
                >
                  450 €
                </span>
                <span className="overline text-[#FAF8F3]/70">IVA incl.</span>
              </div>
              <p className="mt-8 font-sans text-sm text-[#FAF8F3]/70 uppercase tracking-[0.2em] font-medium">
                Incluye todo lo del Pase General y además
              </p>
              <ul className={`mt-5 space-y-4 border-t border-[#FAF8F3]/20 ${EVENT.vipSoldOut ? "opacity-70" : ""}`}>
                {vipExtras.map((it, i) => (
                  <li
                    key={i}
                    className="pt-4 flex gap-4 border-b border-[#FAF8F3]/15 pb-4 last:border-b-0"
                    data-testid={`pricing-vip-item-${i}`}
                  >
                    <span className="mt-2 h-1.5 w-1.5 bg-[#B77A4B] shrink-0" aria-hidden />
                    <span className="font-sans text-base leading-relaxed text-[#FAF8F3]/95">
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                {EVENT.vipSoldOut ? (
                  <div className="space-y-4" data-testid="pricing-vip-soldout">
                    <div
                      className="w-full inline-flex items-center justify-center gap-3 border border-[#FAF8F3]/40 bg-transparent text-[#FAF8F3]/70 px-8 py-4 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium cursor-not-allowed select-none"
                      aria-disabled="true"
                    >
                      Plazas agotadas
                    </div>
                    <p className="font-sans text-xs md:text-sm italic text-[#FAF8F3]/70 text-center leading-relaxed">
                      La Experiencia VIP para esta edición ha completado aforo.
                      Déjanos tus datos para reservar prioridad en la próxima
                      edición.
                    </p>
                    <a
                      href="#info"
                      className="block text-center overline text-[#B77A4B] hover:text-[#FAF8F3] transition-colors"
                      data-testid="pricing-vip-waitlist"
                    >
                      Unirme a la lista de espera →
                    </a>
                  </div>
                ) : (
                  <EditorialButton
                    as="button"
                    type="button"
                    onClick={() => openReservation(STRIPE_LINKS.vip, "Experiencia VIP")}
                    variant="earth"
                    className="w-full"
                    data-testid="pricing-vip-cta"
                  >
                    Reservar Experiencia VIP
                  </EditorialButton>
                )}
              </div>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
